import { styled } from '@linaria/react'
import React, { FC } from 'react'
import { Outlet } from 'react-router'
import { BottomNavigation } from '../ui/BottomNavigation'
import { colors, ColorValue } from '../styles/colors'
import { Wallet } from '../components/Wallet'
import { LogoBlock } from '../components/LogoBlock'
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react'
import { ButtonRegular } from '../ui/Button'
import { FormattedMessage } from 'react-intl'
import { RouterLink } from '../ui/RouterLink'
import { languagePagePath } from '../pages/LanguagePage'
import { detectBrowserLanguage } from '../functions/detectBrowserLanguage'
import { Typography } from '../ui/Typography'

export type HeaderMode = 'hide' | 'full' | 'with-logo'

export const MainLayout: FC<{
  headerMode: HeaderMode
  isShowBottomNavigation: boolean
  background?: ColorValue
}> = ({ background = colors.white, headerMode, isShowBottomNavigation }) => {
  const [tonConnectUI] = useTonConnectUI()
  const wallet = useTonWallet()
  const language = detectBrowserLanguage()

  const handleWalletDisconnect = async () => {
    try {
      await tonConnectUI.disconnect()
    } catch (error) {
      console.error('Failed to disconnect wallet:', error)
    }
  }

  return (
    <MainLayoutWrapper background={background}>
      {headerMode !== 'hide' && (
        <MainLayoutHeader>
          {headerMode === 'with-logo' && <LogoBlock />}
          <WalletWrapper isFullMode={headerMode === 'full'}>
            <Wallet isAvatarFirst={headerMode === 'full'} />
          </WalletWrapper>

          {headerMode === 'full' && Boolean(wallet) && (
            <DisconnectButton onClick={handleWalletDisconnect}>
              <FormattedMessage
                id="wallet.disconnect"
                defaultMessage="Отключиться"
              />
            </DisconnectButton>
          )}

          {headerMode === 'full' && (
            <LanguageLink to={languagePagePath}>
              <Typography
                fontSize={14}
                fontWeight={700}
                color={colors.secondary}
              >
                {language.toUpperCase()}
              </Typography>
            </LanguageLink>
          )}
        </MainLayoutHeader>
      )}

      <MainLayoutInner $withBottomNavigation={isShowBottomNavigation}>
        <Outlet />
      </MainLayoutInner>

      {isShowBottomNavigation && <BottomNavigation />}
    </MainLayoutWrapper>
  )
}

const MainLayoutWrapper = styled.div<{ background: string }>`
  --bottom-navigation-height: 75px;
  min-height: 100%;
  min-width: 320px;
  background: ${props => props.background};
  display: flex;
  flex-direction: column;
`

const MainLayoutInner = styled.div<{ $withBottomNavigation: boolean }>`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  min-height: ${({ $withBottomNavigation }) =>
    $withBottomNavigation
      ? 'calc(100vh - var(--bottom-navigation-height))'
      : '100vh'};
  width: 100%;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;

  padding: ${({ $withBottomNavigation }) =>
    $withBottomNavigation
      ? '12px 20px calc(var(--bottom-navigation-height) + 16px)'
      : '12px 20px 0'};

  @media (max-width: 360px) {
    padding: ${({ $withBottomNavigation }) =>
      $withBottomNavigation
        ? '12px 16px calc(var(--bottom-navigation-height) + 16px)'
        : '12px 16px 0'};
  }

  @media (max-width: 340px) {
    padding: ${({ $withBottomNavigation }) =>
      $withBottomNavigation
        ? '12px 12px calc(var(--bottom-navigation-height) + 16px)'
        : '12px 12px 0'};
  }
`

const MainLayoutHeader = styled.header`
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1;
  gap: 4px;
  border-bottom: 1px solid ${colors.grey200};
  min-height: 50px;

  @media (max-width: 360px) {
    padding: 12px 12px;
    gap: 2px;
  }
`

const WalletWrapper = styled.div<{ isFullMode: boolean }>`
  flex-grow: ${props => (props.isFullMode ? '1' : '0')};
`
const DisconnectButton = styled(ButtonRegular)`
  border: 1px solid ${colors.secondary};
  padding: 8px 12px;
  border-radius: 100px;
`

const LanguageLink = styled(RouterLink)`
  padding: 8px 12px;
  border-radius: 100px;
  border: 1px solid ${colors.secondary};
`
